import { Button, Card, CardBody, Chip } from '@nextui-org/react';
import {
  ExternalProfile,
  ExternalReceiverProfile,
} from '../../../common/src/api/external-profiles';
import { useTranslation } from '../../../common/src/hooks/useTranslation';

interface ProfileSelectionPageProps {
  profiles: ExternalProfile[];
  onSelect: (profile: ExternalProfile) => void;
}

const ProfileSelectionPage = ({
  profiles,
  onSelect,
}: ProfileSelectionPageProps) => {
  const { t } = useTranslation('receiver');

  const getDescription = (profile: ExternalProfile) => {
    if (profile.type === 'market') {
      return t('profileSelection.marketAccess');
    }

    const receiverProfile = profile as ExternalReceiverProfile;
    const relationshipCount = receiverProfile.relationships.length;
    return t(
      relationshipCount === 1
        ? 'profileSelection.exporterRelationship_one'
        : 'profileSelection.exporterRelationship_other',
      { count: relationshipCount }
    );
  };

  return (
    <div className='min-h-screen bg-default-100 flex items-center justify-center p-6'>
      <div className='w-full max-w-3xl'>
        <div className='mb-6'>
          <img src='/full-logo.png' alt='FruitMarkets' className='h-7 mb-6' />
          {/* design-ignore: selección de perfil tras el login, fuera del layout del portal */}
          <h1 className='text-2xl font-semibold text-foreground'>
            {t('profileSelection.title')}
          </h1>
          <p className='text-sm text-default-500 mt-1'>
            {t('profileSelection.subtitle')}
          </p>
        </div>
        <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
          {profiles.map((profile) => (
            <Card key={profile.key} shadow='sm'>
              <CardBody className='gap-4 p-6'>
                <div className='flex items-start justify-between gap-3'>
                  <div>
                    <p className='text-lg font-semibold text-foreground'>
                      {profile.label}
                    </p>
                    <p className='text-sm text-default-500'>
                      {getDescription(profile)}
                    </p>
                  </div>
                  <Chip variant='flat'>
                    {profile.type === 'market'
                      ? t('profileSelection.market')
                      : t('profileSelection.receiver')}
                  </Chip>
                </div>
                {profile.type === 'receiver' && (
                  <div className='space-y-1 text-sm text-default-600'>
                    {(profile as ExternalReceiverProfile).relationships
                      .slice(0, 3)
                      .map((relationship) => (
                        <p key={relationship.access_id} className='truncate'>
                          {relationship.exporter_name} /{' '}
                          {relationship.client_name}
                        </p>
                      ))}
                  </div>
                )}
                <Button color='primary' onPress={() => onSelect(profile)}>
                  {t('profileSelection.enter')}
                </Button>
              </CardBody>
            </Card>
          ))}
        </div>
      </div>
    </div>
  );
};

export default ProfileSelectionPage;
